import { AbsoluteFill } from "remotion";
import {
  Callout,
  Caption,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, MEASURE, COST, THINGATE, DOUBT, FIX, LESSON, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 92 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1620,
    }}
  >
    {children}
  </div>
);

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 30,
      color: COLORS.dim,
      marginTop: 26,
      textAlign: "center",
      maxWidth: 1400,
    }}
  >
    {children}
  </div>
);

// ── Scene 01 — Hook: the treadmill ────────────────────────────────────────────
export const Scene01Hook: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="tuning by vibes" color={COLORS.amber} />
        <Big>
          I kept <span style={{ color: COLORS.amber }}>retuning</span> my RAG.
          <br />
          Fix one question — <span style={{ color: COLORS.red }}>break another</span>.
        </Big>
        <div
          style={{
            display: "flex",
            gap: 14,
            marginTop: 40,
            fontFamily: FONTS.mono,
            fontSize: 26,
            color: COLORS.dim,
            flexWrap: "wrap",
            justifyContent: "center",
            maxWidth: 1500,
          }}
        >
          {["query rewrite", "decomposition", "CRAG", "HyDE", "rerank", "file-diversity", "contextual-retrieval", "graph"].map(
            (k) => (
              <span key={k} style={{ border: `1px solid ${COLORS.line}`, borderRadius: 8, padding: "6px 14px" }}>
                {k}
              </span>
            ),
          )}
        </div>
        <Sub>
          the number of <span style={{ color: COLORS.amber }}>knobs</span> was the disease
        </Sub>
      </div>
    </SceneTransition>
    <Caption lines={HOOK} accent="amber" />
    <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
  </AbsoluteFill>
);

// ── Scene 02 — Measure: the first real baseline ───────────────────────────────
export const Scene02Measure: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="you can't fix what you don't measure" color={COLORS.cyan} />
        <Big size={78}>The eval existed. I'd never turned it on.</Big>
        <div style={{ display: "flex", gap: 34, marginTop: 48 }}>
          <Panel title="pass rate" accent="green">
            <MetricCounter value={22} suffix=" / 23" durationFrames={40} accent="green" fontSize={120} />
          </Panel>
          <Panel title="avg latency (prod)" accent="amber">
            <MetricCounter value={7} prefix="~" suffix="s" durationFrames={40} accent="amber" fontSize={120} />
          </Panel>
          <Panel title="avg relevance" accent="cyan">
            <MetricCounter value={0.4291} decimals={3} durationFrames={50} accent="cyan" fontSize={120} />
          </Panel>
        </div>
        <Sub>
          pass/fail was the wrong signal — <span style={{ color: COLORS.amber }}>latency</span> and{" "}
          <span style={{ color: COLORS.cyan }}>score</span> are the line in the sand
        </Sub>
      </div>
    </SceneTransition>
    <Caption lines={MEASURE} accent="cyan" />
    <SlideTimeBar sceneDurationFrames={SCENE.measure.duration} />
  </AbsoluteFill>
);

// ── Scene 03 — Cost: $5/week, one flag off ────────────────────────────────────
export const Scene03Cost: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="the money — not the embeddings" color={COLORS.amber} />
        <Big size={80}>
          One <span style={{ color: COLORS.amber }}>LLM call</span> per chunk, at sync.
        </Big>
        <div style={{ display: "flex", gap: 34, marginTop: 46, alignItems: "center" }}>
          <Panel title="new chunks / week" accent="violet">
            <MetricCounter value={17000} prefix="~" durationFrames={55} accent="violet" fontSize={104} />
          </Panel>
          <div style={{ fontFamily: FONTS.display, fontSize: 64, color: COLORS.dim }}>→</div>
          <Panel title="cost / week" accent="amber">
            <MetricCounter value={5} prefix="$" durationFrames={45} accent="amber" fontSize={120} />
          </Panel>
          <div style={{ fontFamily: FONTS.display, fontSize: 64, color: COLORS.dim }}>→</div>
          <Callout label="CONTEXTUAL_RETRIEVAL" accent="green" width={260}>
            one config flip — <span style={{ color: COLORS.green }}>OFF</span>
          </Callout>
        </div>
        <Sub>a one-sentence "context note" written by gemini, for every chunk — a sticky note per page</Sub>
      </div>
    </SceneTransition>
    <Caption lines={COST} accent="amber" />
    <SlideTimeBar sceneDurationFrames={SCENE.cost.duration} />
  </AbsoluteFill>
);

// ── Scene 04 — Thin-gate: my own fix regressed, the eval caught it ────────────
export const Scene04ThinGate: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="the eval caught me" color={COLORS.red} />
        <Big size={78}>
          I fixed the bug — and made it <span style={{ color: COLORS.red }}>slower</span>.
        </Big>
        <div style={{ display: "flex", gap: 30, marginTop: 46 }}>
          <Panel title="always-on rescue" accent="red">
            <MetricCounter value={1.7} prefix="+" suffix="s" decimals={1} durationFrames={40} accent="red" fontSize={104} />
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 10 }}>
              padded every query · ~0 score gain
            </div>
          </Panel>
          <Panel title="after thin-gate" accent="green">
            <MetricCounter value={-1155} suffix="ms" durationFrames={45} accent="green" fontSize={104} />
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 10 }}>
              recovered · score held
            </div>
          </Panel>
        </div>
        <Sub>
          rule: <span style={{ color: COLORS.green }}>only dig when the basket is nearly empty</span>
        </Sub>
      </div>
    </SceneTransition>
    <Caption lines={THINGATE} accent="green" />
    <SlideTimeBar sceneDurationFrames={SCENE.thingate.duration} />
  </AbsoluteFill>
);

// ── Scene 05 — The 41-second doubt ────────────────────────────────────────────
const DOUBT_NODES: PipeNode[] = [
  { label: "grader", value: "~2s", sub: "LLM checks chunks", accent: "cyan" },
  { label: "broader search", value: "17.6s", sub: "full re-search #1", accent: "amber" },
  { label: "HyDE", value: "21.5s", sub: "imagine + re-search #2", accent: "red" },
];
export const Scene05Doubt: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="one question · 67 seconds" color={COLORS.red} />
        <Big size={84}>
          The quality gate spent <span style={{ color: COLORS.red }}>41 seconds</span> doubting itself.
        </Big>
        <div style={{ marginTop: 44 }}>
          <PipelineGraph nodes={DOUBT_NODES} startFrame={10} stepFrames={22} nodeWidth={300} />
        </div>
        <Sub>
          it re-ran the <span style={{ color: COLORS.amber }}>entire search twice</span> — yet it already had 4 perfect
          sources
        </Sub>
      </div>
    </SceneTransition>
    <Caption lines={DOUBT} accent="red" />
    <SlideTimeBar sceneDurationFrames={SCENE.doubt.duration} />
  </AbsoluteFill>
);

// ── Scene 06 — The fix: measure-then-cut ──────────────────────────────────────
export const Scene06Fix: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="measure, then cut" color={COLORS.green} />
        <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1500 }}>
          <Callout label="strong results" accent="green" width={1400}>
            trust them — skip both re-searches
          </Callout>
          <Callout label="a little unsure" accent="cyan" width={1400}>
            ONE cheap recheck — never the expensive 'imagine the answer' pass
          </Callout>
          <Callout label="found nothing" accent="amber" width={1400}>
            only now run HyDE — the real last resort
          </Callout>
        </div>
        <div style={{ display: "flex", gap: 28, marginTop: 44, alignItems: "center" }}>
          <MetricCounter value={41} suffix="s" durationFrames={1} accent="red" fontSize={96} />
          <div style={{ fontFamily: FONTS.display, fontSize: 70, color: COLORS.dim }}>→</div>
          <MetricCounter value={2} prefix="~" suffix="s" startFrame={20} durationFrames={40} accent="green" fontSize={140} />
        </div>
        <Sub>same answer · same sources · 20× faster on the case that hurt most</Sub>
      </div>
    </SceneTransition>
    <Caption lines={FIX} accent="green" />
    <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
  </AbsoluteFill>
);

// ── Scene 07 — The lesson ─────────────────────────────────────────────────────
export const Scene07Lesson: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Kicker text="the way off the treadmill" color={COLORS.violet} />
        <Big size={86}>
          Don't add knobs.
          <br />
          <span style={{ color: COLORS.green }}>Delete layers</span> until it's boring.
        </Big>
        <Sub>
          every clever layer helps some questions and quietly hurts others — keep only what the{" "}
          <span style={{ color: COLORS.cyan }}>number</span> defends
        </Sub>
      </div>
    </SceneTransition>
    <Caption lines={LESSON} accent="violet" />
    <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
  </AbsoluteFill>
);

// ── Scene 08 — Outro ──────────────────────────────────────────────────────────
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SceneTransition from="up">
      <div style={{ ...center, display: "flex" }}>
        <Big size={108}>
          Stop <span style={{ color: COLORS.amber }}>guessing</span>.
          <br />
          <span style={{ color: COLORS.cyan }}>Measure.</span>
        </Big>
        <Sub>one golden eval · one change at a time · keep it only if the number agrees</Sub>
      </div>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
